import { useEffect, useRef, useState, Fragment } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { FinePrint } from "@/components/FinePrint";
import { UserRecordDrawer } from "@/pages/admin/UserRecordDrawer";
import { CAMPUSES } from "@/lib/institution";
import { ProgramCombobox } from "@/components/ProgramCombobox";
import { PresenceBadge } from "@/components/PresenceBadge";
import { Loader2, KeyRound, Copy, MailCheck, Search, UserPlus, MoreHorizontal, ShieldAlert, Repeat, Upload, Download, Pencil, Check, X, History, Power, ChevronLeft, ChevronRight, ArrowUpDown, Eye, Lock, RefreshCw } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const ROLE_LABEL = { admin: "Website Administrator", advisor: "Academic Advisor", faculty: "Faculty", employer: "Employer", student: "Student" };
const DEFAULT_INVITE = { student: "student_early_preview", employer: "employer_early_preview", advisor: "team_early_preview", faculty: "team_early_preview", admin: "team_early_preview" };
const INVITE_OPTIONS = [
  { v: "standard", l: "Standard account activation" },
  { v: "team_early_preview", l: "Team Member — Early GPA+ Preview" },
  { v: "student_early_preview", l: "Student — Early GPA+ Preview" },
  { v: "employer_early_preview", l: "Employer — Early GPA+ Preview" },
];
const ASSIGNABLE = [
  { v: "student", l: "Student" }, { v: "faculty", l: "Faculty" },
  { v: "advisor", l: "Academic Advisor" }, { v: "employer", l: "Employer" },
];
const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");
const EDIT_FIELDS = ["campus", "program", "job_title", "department", "company", "employee_id", "student_number"];

export function AdminUsers() {
  const { user } = useAuth();
  const can = (p) => (user?.permissions || []).includes(p);
  const canView = can("users.view");
  const canManage = can("users.roles.manage");

  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("last_name");
  const [dir, setDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [busy, setBusy] = useState(null);

  const [result, setResult] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ first_name: "", last_name: "", email: "", role: "student", institution: "", id_number: "", campus: "" });
  const [changing, setChanging] = useState(null);
  const [newRole, setNewRole] = useState("student");
  const [reason, setReason] = useState("");
  const [editing, setEditing] = useState(null); // user_id
  const [editForm, setEditForm] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [auditRows, setAuditRows] = useState(null);
  const [recordId, setRecordId] = useState(null);
  const [invite, setInvite] = useState({ template: "", subject: "", body: "", open: false, busy: false });
  const [setPass, setSetPass] = useState(null); // target user
  const [passForm, setPassForm] = useState({ password: "", must_change: true });
  const [passResult, setPassResult] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    const params = { page, page_size: pageSize, sort, dir };
    if (q) params.q = q;
    if (role !== "all") params.role = role;
    if (status !== "all") params.status = status;
    api.get("/admin/users", { params }).then(({ data }) => setData(data)).catch(() => setData({ items: [], total: 0, page: 1, pages: 1 }));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [role, status, sort, dir, page, pageSize]);

  const search = () => { setPage(1); load(); };
  const setSortCol = (col) => {
    if (sort === col) setDir(dir === "asc" ? "desc" : "asc");
    else { setSort(col); setDir("asc"); }
    setPage(1);
  };

  const sendReset = async (u) => {
    setBusy(u.user_id);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/reset-password`);
      setResult({ ...data, name: u.name });
    } catch { toast.error("Could not create a reset link"); }
    finally { setBusy(null); }
  };
  const activate = async (u) => {
    setBusy(u.user_id);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/activate`);
      setResult({ ...data, name: u.name, activated: true });
      toast.success(data.emailed ? `Activated — set-password link emailed to ${u.email}` : `Activated — share the link`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not activate"); }
    finally { setBusy(null); }
  };
  const copyLink = () => { if (result?.reset_link) { navigator.clipboard.writeText(result.reset_link); toast.success("Link copied"); } };

  const openSetPass = (u) => { setSetPass(u); setPassForm({ password: "", must_change: true }); };
  const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let p = "";
    const arr = new Uint32Array(12);
    (window.crypto || window.msCrypto).getRandomValues(arr);
    for (let i = 0; i < 12; i++) p += chars[arr[i] % chars.length];
    setPassForm((f) => ({ ...f, password: p }));
  };
  const submitSetPass = async () => {
    if (passForm.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setBusy("setpass");
    try {
      const { data } = await api.post(`/admin/users/${setPass.user_id}/set-password`, {
        password: passForm.password, must_change: passForm.must_change,
      });
      setPassResult({ email: data.email, name: setPass.name, password: passForm.password, must_change: data.must_change });
      setSetPass(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not set password"); }
    finally { setBusy(null); }
  };
  const copyPassword = () => { if (passResult?.password) { navigator.clipboard.writeText(passResult.password); toast.success("Password copied"); } };

  const submitAdd = async (e) => {
    e.preventDefault();
    setBusy("add");
    try {
      const payload = { ...addForm };
      if (invite.template && invite.template !== "standard") payload.invite_template = invite.template;
      if (invite.open) payload.email_override = { subject: invite.subject, body: invite.body };
      const { data } = await api.post("/admin/users", payload);
      toast.success(data.emailed ? "User created — invitation emailed" : "User created — activation link generated");
      setAddOpen(false);
      setAddForm({ first_name: "", last_name: "", email: "", role: "student", institution: "", id_number: "", campus: "" });
      setInvite({ template: "", subject: "", body: "", open: false, busy: false });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not create user"); }
    finally { setBusy(null); }
  };

  const previewInvite = async () => {
    setInvite((s) => ({ ...s, busy: true }));
    try {
      const key = (invite.template && invite.template !== "standard") ? invite.template : "registration_approved";
      const { data } = await api.post("/admin/email/draft", { template_key: key, ctx: { first_name: addForm.first_name || "there", last_name: addForm.last_name || "", email: addForm.email || "" } });
      setInvite((s) => ({ ...s, subject: data.subject, body: data.body, open: true, busy: false }));
    } catch { toast.error("Could not generate preview"); setInvite((s) => ({ ...s, busy: false })); }
  };

  const openChange = (u) => { setChanging(u); setNewRole(u.role === "admin" ? "student" : u.role); setReason(""); };
  const submitChange = async () => {
    setBusy("change");
    try {
      await api.post(`/admin/users/${changing.user_id}/role`, { new_role: newRole, reason });
      toast.success(`Role changed to ${ROLE_LABEL[newRole]}`);
      setChanging(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not change role"); }
    finally { setBusy(null); }
  };

  const startEdit = (u) => {
    setEditing(u.user_id);
    setEditForm({ account_status: u.account_status || "active", ...Object.fromEntries(EDIT_FIELDS.map((k) => [k, u[k] || ""])) });
  };
  const saveEdit = async (u) => {
    setBusy(u.user_id);
    try {
      await api.patch(`/admin/users/${u.user_id}`, editForm);
      toast.success("Account updated");
      setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); }
    finally { setBusy(null); }
  };

  const openAudit = async (u) => {
    setAuditFor(u); setAuditRows(null);
    try { const { data } = await api.get(`/admin/users/${u.user_id}/audit`); setAuditRows(data); }
    catch { setAuditRows([]); }
  };

  const openRecord = (u) => setRecordId(u.user_id);

  const exportCsv = async () => {
    setBusy("export");
    try {
      const params = { sort, dir };
      if (q) params.q = q;
      if (role !== "all") params.role = role;
      if (status !== "all") params.status = status;
      const res = await api.get("/admin/users/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "gpaplus_users.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
    finally { setBusy(null); }
  };

  const runPreview = async (file) => {
    setImportFile(file); setBusy("import"); setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/users/bulk?dry_run=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Preview failed"); }
    finally { setBusy(null); }
  };
  const confirmImport = async () => {
    if (!importFile) return;
    setBusy("import");
    try {
      const fd = new FormData(); fd.append("file", importFile);
      const { data } = await api.post("/admin/users/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data); setImportFile(null);
      toast.success(`Imported ${data.created} • skipped ${data.skipped_existing} • invalid ${data.invalid}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import failed"); }
    finally { setBusy(null); if (fileRef.current) fileRef.current.value = ""; }
  };
  const downloadTemplate = () => {
    const csv = "first_name,last_name,email,role,campus,program,job_title,department,institution,id_number\n"
      + "Jane,Doe,jane.doe@student.lonestar.edu,student,North Harris,A.A.S. Nursing,,,,S12345678\n"
      + "John,Smith,john.smith@lonestar.edu,advisor,CyFair,,Academic Advisor,Advising,,E1002003\n"
      + "Acme,Recruiter,recruiter@acme.com,employer,,,Talent Lead,,Acme Corp,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "gpaplus_user_import_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const items = data?.items || [];
  const SortHead = ({ col, children, className }) => (
    <TableHead className={className}>
      <button onClick={() => setSortCol(col)} className="inline-flex items-center gap-1 font-semibold hover:text-[#003057]" data-testid={`users-sort-${col}`}>
        {children} <ArrowUpDown className={`h-3 w-3 ${sort === col ? "text-[#78be21]" : "text-slate-300"}`} />
      </button>
    </TableHead>
  );

  return (
    <DashboardShell title="User Accounts" subtitle="Directory, roles, status, inline edits, bulk import & audit" nav={ADMIN_NAV}>
      {!data && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {data && !canView && (
        <Card className="p-8 text-center card-elevate" data-testid="users-preview-notice">
          <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
          <p className="mt-3 font-bold text-[#003057]">Read-only preview</p>
          <p className="mt-1 text-sm text-slate-500">User account details require Website Administrator access.</p>
        </Card>
      )}
      {data && canView && (
        <div className="space-y-5">
          <Card className="p-4 card-elevate">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="Search name, email or ID" className="pl-9" data-testid="users-search" />
              </div>
              <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
                <SelectTrigger className="w-44" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="faculty">Faculty</SelectItem>
                  <SelectItem value="advisor">Academic Advisor</SelectItem>
                  <SelectItem value="admin">Website Administrator</SelectItem>
                  <SelectItem value="employer">Employer</SelectItem>
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                <SelectTrigger className="w-36" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={search} variant="outline" data-testid="users-search-btn">Search</Button>
              <Button onClick={exportCsv} variant="outline" disabled={busy === "export"} data-testid="users-export-btn">
                {busy === "export" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Export
              </Button>
              {can("users.create") && (
                <Button onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} variant="outline" data-testid="users-import-btn">
                  <Upload className="mr-1.5 h-4 w-4" /> Import
                </Button>
              )}
              {can("users.create") && (
                <Button onClick={() => setAddOpen(true)} data-testid="add-user-btn" className="bg-[#003057] hover:bg-[#00223f]">
                  <UserPlus className="mr-1.5 h-4 w-4" /> Add User
                </Button>
              )}
            </div>
          </Card>

          <Card className="p-2 card-elevate sm:p-4">
            <div className="mb-2 flex items-center justify-between px-2 pt-2 text-xs text-slate-400">
              <span data-testid="users-total">{data.total.toLocaleString()} account{data.total === 1 ? "" : "s"}</span>
              <div className="flex items-center gap-2">
                <span>Rows</span>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="h-7 w-[70px]" data-testid="users-page-size"><SelectValue /></SelectTrigger>
                  <SelectContent>{[25, 50, 100, 250, 500, 1000].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="overflow-x-auto">
            <Table className="text-[13px]">
              <TableHeader><TableRow className="[&_th]:h-9 [&_th]:text-[11px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-slate-400">
                <SortHead col="last_name">Last Name</SortHead>
                <SortHead col="first_name">First Name</SortHead>
                <TableHead>Title</TableHead>
                <SortHead col="email">Email</SortHead>
                <SortHead col="role">Role</SortHead>
                <SortHead col="status">Status</SortHead>
                <TableHead>Campus</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>ID</TableHead>
                <SortHead col="last_active">Last Active</SortHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((u) => (
                  <Fragment key={u.user_id}>
                  <TableRow data-testid={`user-row-${u.user_id}`} className="cursor-pointer [&_td]:py-2 hover:bg-slate-50" onClick={() => openRecord(u)}>
                    <TableCell className="font-medium text-[#003057]">{u.last_name || "—"}</TableCell>
                    <TableCell className="text-slate-700">{u.first_name || "—"}</TableCell>
                    <TableCell className="text-slate-600">{u.job_title || "—"}</TableCell>
                    <TableCell className="text-slate-600">{u.email}</TableCell>
                    <TableCell className="text-slate-600">{ROLE_LABEL[u.role] || u.role}</TableCell>
                    <TableCell>
                      <span className={`font-medium ${u.account_status === "inactive" ? "text-slate-400" : "text-emerald-600"}`} data-testid={`user-status-${u.user_id}`}>
                        {u.account_status === "inactive" ? "Inactive" : "Active"}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600">{u.campus || "—"}</TableCell>
                    <TableCell className="text-slate-600">{u.program || "—"}</TableCell>
                    <TableCell className="font-mono-num text-xs text-slate-500">{u.employee_id || u.student_number || "—"}</TableCell>
                    <TableCell data-testid={`user-lastactive-${u.user_id}`}><PresenceBadge lastActiveAt={u.last_active_at} /></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" data-testid={`user-actions-${u.user_id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canManage && (
                            <DropdownMenuItem onClick={() => startEdit(u)} data-testid={`edit-btn-${u.user_id}`} className="cursor-pointer">
                              <Pencil className="mr-2 h-4 w-4" /> Edit info
                            </DropdownMenuItem>
                          )}
                          {canManage && u.account_status === "inactive" && (
                            <DropdownMenuItem disabled={busy === u.user_id} onClick={() => activate(u)} data-testid={`activate-btn-${u.user_id}`} className="cursor-pointer text-[#5fa114]">
                              <Power className="mr-2 h-4 w-4" /> Activate & send link
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem disabled={busy === u.user_id} onClick={() => sendReset(u)} data-testid={`reset-btn-${u.user_id}`} className="cursor-pointer">
                            <KeyRound className="mr-2 h-4 w-4" /> Send reset link
                          </DropdownMenuItem>
                          {canManage && u.role !== "admin" && (
                            <DropdownMenuItem onClick={() => openSetPass(u)} data-testid={`set-password-btn-${u.user_id}`} className="cursor-pointer">
                              <Lock className="mr-2 h-4 w-4" /> Set password
                            </DropdownMenuItem>
                          )}
                          {canManage && u.role !== "admin" && (
                            <DropdownMenuItem onClick={() => openChange(u)} data-testid={`change-role-btn-${u.user_id}`} className="cursor-pointer">
                              <Repeat className="mr-2 h-4 w-4" /> Change role
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openAudit(u)} data-testid={`audit-btn-${u.user_id}`} className="cursor-pointer">
                            <History className="mr-2 h-4 w-4" /> Change log
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  {editing === u.user_id && (
                    <TableRow key={`${u.user_id}-edit`} data-testid={`user-edit-${u.user_id}`}>
                      <TableCell colSpan={11} className="bg-slate-50/70">
                        <div className="grid grid-cols-2 gap-3 p-2 sm:grid-cols-4">
                          <div>
                            <Label className="text-[11px]">Status</Label>
                            <Select value={editForm.account_status} onValueChange={(v) => setEditForm((f) => ({ ...f, account_status: v }))}>
                              <SelectTrigger className="mt-1 h-8" data-testid={`edit-status-${u.user_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                            </Select>
                          </div>
                          {[["campus", "Campus"], ["program", "Program"], [u.role === "student" ? "student_number" : "employee_id", u.role === "student" ? "Student ID" : "Employee ID"], ["job_title", "Job title"], ["department", "Department"], ["company", "Company / Org"]].map(([k, l]) => (
                            <div key={k}>
                              <Label className="text-[11px]">{l}</Label>
                              {k === "campus" ? (
                                <Select value={editForm.campus || ""} onValueChange={(v) => setEditForm((f) => ({ ...f, campus: v === "__none" ? "" : v }))}>
                                  <SelectTrigger className="mt-1 h-8" data-testid={`edit-campus-${u.user_id}`}><SelectValue placeholder="Campus" /></SelectTrigger>
                                  <SelectContent><SelectItem value="__none">Not specified</SelectItem>{CAMPUSES.map((c) => <SelectItem key={c} value={c}>LSC-{c}</SelectItem>)}</SelectContent>
                                </Select>
                              ) : k === "program" ? (
                                <ProgramCombobox value={editForm.program || ""} onChange={(v) => setEditForm((f) => ({ ...f, program: v }))} testid={`edit-program-${u.user_id}`} size="sm" />
                              ) : (
                                <Input value={editForm[k] || ""} onChange={(e) => setEditForm((f) => ({ ...f, [k]: e.target.value }))} className="mt-1 h-8" data-testid={`edit-${k}-${u.user_id}`} />
                              )}
                            </div>
                          ))}
                          <div className="col-span-2 flex items-end gap-2 sm:col-span-4">
                            <Button size="sm" onClick={() => saveEdit(u)} disabled={busy === u.user_id} className="bg-[#5fa114] hover:bg-[#4f8a10]" data-testid={`edit-save-${u.user_id}`}>
                              {busy === u.user_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1 h-4 w-4" /> Save</>}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditing(null)} data-testid={`edit-cancel-${u.user_id}`}><X className="mr-1 h-4 w-4" /> Cancel</Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </Fragment>
                ))}
                {items.length === 0 && <TableRow><TableCell colSpan={11} className="py-8 text-center text-slate-400" data-testid="users-empty">No accounts match your search.</TableCell></TableRow>}
              </TableBody>
            </Table>
            </div>

            <div className="mt-3 flex items-center justify-between px-2">
              <span className="text-xs text-slate-400">Page {data.page} of {data.pages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="users-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="users-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </Card>

          <FinePrint>
            Institutional information (campus, program, job title, department, organization, IDs) and account status are
            admin-managed here and are read-only in each user's My Profile. Website Administrator authority is held only by the
            site owner and is never assignable. Every edit, activation and role change is recorded per user in the Change log.
          </FinePrint>
        </div>
      )}

      {/* Add User dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (o) setInvite({ template: DEFAULT_INVITE[addForm.role] || "standard", subject: "", body: "", open: false, busy: false }); }}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#003057]">Add a user</DialogTitle></DialogHeader>
          <form onSubmit={submitAdd} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First name</Label><Input value={addForm.first_name} onChange={(e) => setAddForm((f) => ({ ...f, first_name: e.target.value }))} required data-testid="add-user-first" className="mt-1.5" /></div>
              <div><Label>Last name</Label><Input value={addForm.last_name} onChange={(e) => setAddForm((f) => ({ ...f, last_name: e.target.value }))} required data-testid="add-user-last" className="mt-1.5" /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} required data-testid="add-user-email" className="mt-1.5" /></div>
            <div>
              <Label>Role</Label>
              <Select value={addForm.role} onValueChange={(v) => { setAddForm((f) => ({ ...f, role: v })); setInvite((s) => ({ ...s, template: DEFAULT_INVITE[v] || "standard", open: false })); }}>
                <SelectTrigger className="mt-1.5" data-testid="add-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ASSIGNABLE.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {addForm.role === "employer" && (
              <div><Label>Organization</Label><Input value={addForm.institution} onChange={(e) => setAddForm((f) => ({ ...f, institution: e.target.value }))} data-testid="add-user-org" className="mt-1.5" /></div>
            )}
            {addForm.role !== "employer" && (
              <div>
                <Label>Campus <span className="text-slate-400">(optional)</span></Label>
                <Select value={addForm.campus || ""} onValueChange={(v) => setAddForm((f) => ({ ...f, campus: v === "__none" ? "" : v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="add-user-campus"><SelectValue placeholder="Select campus" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none">Not specified</SelectItem>{CAMPUSES.map((c) => <SelectItem key={c} value={c}>LSC-{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>{addForm.role === "student" ? "Student ID" : "Employee ID"} <span className="text-slate-400">(optional)</span></Label><Input value={addForm.id_number} onChange={(e) => setAddForm((f) => ({ ...f, id_number: e.target.value }))} data-testid="add-user-id" className="mt-1.5" /></div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
              <Label>Invitation email</Label>
              <Select value={invite.template || "standard"} onValueChange={(v) => setInvite((s) => ({ ...s, template: v, open: false }))}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="add-user-invite-template"><SelectValue /></SelectTrigger>
                <SelectContent>{INVITE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
              <div className="mt-2 flex items-center gap-2">
                <Button type="button" size="sm" variant="outline" onClick={previewInvite} disabled={invite.busy} data-testid="add-user-preview-invite">
                  {invite.busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Eye className="mr-1.5 h-4 w-4" />} Preview & personalize
                </Button>
                {invite.open && <span className="text-[11px] text-slate-400">Editing here won't change the master template.</span>}
              </div>
              {invite.open && (
                <div className="mt-3 space-y-2" data-testid="add-user-invite-editor">
                  <div><Label className="text-[11px]">Subject</Label><Input value={invite.subject} onChange={(e) => setInvite((s) => ({ ...s, subject: e.target.value }))} className="mt-1 bg-white" data-testid="add-user-invite-subject" /></div>
                  <div><Label className="text-[11px]">Message</Label><Textarea value={invite.body} onChange={(e) => setInvite((s) => ({ ...s, body: e.target.value }))} className="mt-1 min-h-[160px] bg-white" data-testid="add-user-invite-body" /></div>
                  <p className="text-[11px] text-slate-400">Keep <code>{"{{activation_link}}"}</code> — it becomes the recipient's secure access link automatically.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy === "add"} className="bg-[#003057] hover:bg-[#00223f]" data-testid="add-user-submit">
                {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : (invite.open ? "Confirm & send invitation" : "Create & send invitation")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#003057]">Bulk import users</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-slate-600">Columns: <code className="text-xs">first_name, last_name, email, role, campus, program, job_title, department, institution, id_number</code></p>
              <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="import-template-btn"><Download className="mr-1.5 h-4 w-4" /> Template</Button>
            </div>
            <p className="text-xs text-slate-500">Accounts are created <b>Inactive</b> with no email sent — activate/invite later. Existing emails are skipped. Up to 10,000 rows. Choosing a file runs a <b>preview</b> first — nothing is saved until you confirm.</p>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={(e) => { const f = e.target.files?.[0]; if (f) runPreview(f); }} className="block w-full text-sm" data-testid="import-file" />
            {busy === "import" && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Working…</p>}
            {importResult && (
              <div className={`rounded-lg border p-3 text-sm ${importResult.dry_run ? "border-sky-200 bg-sky-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="import-result">
                <p className="font-semibold text-[#003057]">{importResult.dry_run ? "Preview — nothing saved yet" : "Import complete"}</p>
                <p className="mt-1"><b className="text-[#5fa114]">{importResult.created}</b> {importResult.dry_run ? "will be created" : "created"} · <b>{importResult.skipped_existing}</b> skipped (existing) · <b className="text-amber-600">{importResult.invalid}</b> invalid · {importResult.total} rows</p>
                {importResult.preview?.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-auto rounded border border-slate-200 bg-white" data-testid="import-preview">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-slate-400"><th className="p-1.5">Name</th><th className="p-1.5">Email</th><th className="p-1.5">Role</th></tr></thead>
                      <tbody>{importResult.preview.map((p, i) => (<tr key={i} className="border-t border-slate-100"><td className="p-1.5">{p.name}</td><td className="p-1.5">{p.email}</td><td className="p-1.5 capitalize">{p.role}</td></tr>))}</tbody>
                    </table>
                  </div>
                )}
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 max-h-32 space-y-1 overflow-y-auto text-xs text-amber-700">
                    {importResult.errors.map((e, i) => <li key={i}>Row {e.row}: {e.email || "—"} — {e.reason}</li>)}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              {importResult?.dry_run && importResult.created > 0 && (
                <Button onClick={confirmImport} disabled={busy === "import"} className="bg-[#5fa114] hover:bg-[#4f8a10]" data-testid="import-confirm-btn">
                  {busy === "import" ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm import (${importResult.created})`}
                </Button>
              )}
              <Button variant="outline" onClick={() => setImportOpen(false)}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Role dialog */}
      <Dialog open={!!changing} onOpenChange={(o) => !o && setChanging(null)}>
        <DialogContent data-testid="change-role-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#003057]">Change role</DialogTitle></DialogHeader>
          {changing && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-[#003057]">{changing.name}</span> ({changing.email})<br />
                Current role: <b>{ROLE_LABEL[changing.role] || changing.role}</b>
              </p>
              <div>
                <Label>New role</Label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="mt-1.5" data-testid="change-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSIGNABLE.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Reason / note <span className="text-slate-400">(optional)</span></Label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" data-testid="change-role-reason" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setChanging(null)}>Cancel</Button>
                <Button onClick={submitChange} disabled={busy === "change" || newRole === changing.role} className="bg-[#003057] hover:bg-[#00223f]" data-testid="change-role-submit">
                  {busy === "change" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm role change"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change log dialog */}
      <Dialog open={!!auditFor} onOpenChange={(o) => !o && setAuditFor(null)}>
        <DialogContent data-testid="audit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#003057]">Change log — {auditFor?.name}</DialogTitle></DialogHeader>
          {!auditRows && <div className="flex h-24 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#003057]" /></div>}
          {auditRows && auditRows.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No changes recorded yet.</p>}
          {auditRows && auditRows.length > 0 && (
            <ul className="max-h-80 space-y-2 overflow-y-auto">
              {auditRows.map((l, i) => (
                <li key={i} className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  <span className="font-semibold text-[#003057]">{l.action}</span> · {l.actor} · {String(l.at).slice(0, 16).replace("T", " ")}
                  {Object.keys(l.changes || {}).length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {Object.entries(l.changes).map(([f, c]) => <li key={f}>{f}: <span className="text-slate-400">{c.from || "—"}</span> → <b>{c.to || "—"}</b></li>)}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset / activation link result */}
      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent data-testid="reset-result-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#003057]">{result?.activated ? "Account activated" : "Reset link ready"}</DialogTitle></DialogHeader>
          {result && (
            <div className="space-y-4">
              <p className="flex items-center gap-2 text-sm text-slate-600">
                <MailCheck className={`h-5 w-5 ${result.emailed ? "text-[#5fa114]" : "text-amber-500"}`} />
                {result.emailed
                  ? <>Emailed to <span className="font-semibold text-[#003057]">{result.email}</span>. Expires in {result.expires_minutes} minutes.</>
                  : <>Email couldn't be delivered to <span className="font-semibold text-[#003057]">{result.email}</span> — share the link below.</>}
              </p>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                <code className="flex-1 break-all text-xs text-slate-600" data-testid="reset-link-value">{result.reset_link}</code>
                <Button size="sm" onClick={copyLink} className="bg-[#003057] hover:bg-[#00223f]" data-testid="copy-reset-link"><Copy className="mr-1.5 h-4 w-4" /> Copy</Button>
              </div>
              <div className="flex justify-end"><Button variant="outline" onClick={() => setResult(null)} data-testid="reset-result-close">Done</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Set password dialog */}
      <Dialog open={!!setPass} onOpenChange={(o) => !o && setSetPass(null)}>
        <DialogContent data-testid="set-password-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#003057]">Set password</DialogTitle></DialogHeader>
          {setPass && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-[#003057]">{setPass.name}</span> ({setPass.email})<br />
                Set a new password and share it with the user directly. No email is sent.
              </p>
              <div>
                <Label>New password</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input value={passForm.password} onChange={(e) => setPassForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="At least 6 characters" data-testid="set-password-input" />
                  <Button type="button" variant="outline" onClick={genPassword} data-testid="set-password-generate" title="Generate">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={passForm.must_change} onCheckedChange={(v) => setPassForm((f) => ({ ...f, must_change: !!v }))} data-testid="set-password-mustchange" />
                Require the user to change it on next login
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSetPass(null)}>Cancel</Button>
                <Button onClick={submitSetPass} disabled={busy === "setpass" || passForm.password.length < 6} className="bg-[#003057] hover:bg-[#00223f]" data-testid="set-password-submit">
                  {busy === "setpass" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set password"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Set password result */}
      <Dialog open={!!passResult} onOpenChange={(o) => !o && setPassResult(null)}>
        <DialogContent data-testid="set-password-result-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#003057]">Password set</DialogTitle></DialogHeader>
          {passResult && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Password updated for <span className="font-semibold text-[#003057]">{passResult.email}</span>. Copy it now — it won't be shown again.
                {passResult.must_change && <> They'll be prompted to change it on next login.</>}
              </p>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                <code className="flex-1 break-all font-mono-num text-sm text-slate-700" data-testid="set-password-value">{passResult.password}</code>
                <Button size="sm" onClick={copyPassword} className="bg-[#003057] hover:bg-[#00223f]" data-testid="copy-set-password"><Copy className="mr-1.5 h-4 w-4" /> Copy</Button>
              </div>
              <div className="flex justify-end"><Button variant="outline" onClick={() => setPassResult(null)} data-testid="set-password-result-close">Done</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Full user record drawer */}
      <UserRecordDrawer userId={recordId} onClose={() => setRecordId(null)} onNavigate={(id) => setRecordId(id)} canManage={canManage} />
    </DashboardShell>
  );
}
